import React, { useEffect, useCallback, useRef } from 'react';

import {
  useAssignUserNeedDesignInput,
  useAssignDesignInputUserNeed,
  useAssignDesignInputDesignOutput,
  useAssignDesignOutputDesignInput,
  useAssignDesignOutputVerification,
  useAssignVerificationDesignOutput,
  useAssignValidationUserNeed,
  useAssignDesignSpecUserNeed,
  useAssignUserNeedDesignSpec,
  useAssignValidationDesignInput,
  useAssignDesignInputValidation,
  useAssignUserNeedVerification,
  useAssignUserNeedValidation,
  useAssignDesignOutputValidation,
  useAssignDesignInputVerification,
  useAssignDesignInputDesignSpec,
  useAssignDesignOutputDesignSpec
} from '../Product.hooks';
import { FetchError } from '../../../common/api/api';
import { ProductEntity } from '../Product.interface';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';

export interface UnlinkConfirmDialogProps {
  open: boolean;
  productId: number;
  entityId: number;
  to: ProductBoardColumnEntity;
  from: ProductBoardColumnEntity;
  unlinkItemId?: number;
  selectedItems?: ProductEntity[];
  title?: string;
  onError?: (error: FetchError) => void;
  onCancel?: () => void;
  onChange?: (selectedItem: ProductEntity) => void;
}

const UnlinkConfirmDialog: React.FC<UnlinkConfirmDialogProps> = props => {
  const {
    open,
    productId,
    entityId,
    to,
    from,
    unlinkItemId,
    selectedItems,
    title = 'Unlink Item',
    onError,
    onCancel,
    onChange
  } = props;
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);
  const {
    updatedProductUserNeed: updatedProductUserNeedDesignInput,
    error: assignUserNeedDesignInputsError,
    assignDesignInputs: assignUserNeedDesignInputs
  } = useAssignUserNeedDesignInput(productId, entityId);
  const {
    updatedProductUserNeed: updatedProductUserNeedDesignSpec,
    error: assignUserNeedDesignSpecsError,
    assignDesignSpecs: assignUserNeedDesignSpecs
  } = useAssignUserNeedDesignSpec(productId, entityId);
  const {
    updatedProductUserNeed: updatedProductUserNeedVerification,
    error: assignUserNeedVerificationsError,
    assignVerifications: assignUserNeedVerifications
  } = useAssignUserNeedVerification(productId, entityId);
  const {
    updatedProductUserNeed: updatedProductUserNeedValidation,
    error: assignUserNeedValidationsError,
    assignValidations: assignUserNeedValidations
  } = useAssignUserNeedValidation(productId, entityId);
  const {
    updatedProductDesignInput: updatedProductDesignInputUserNeed,
    error: assignUserNeedsError,
    assignUserNeeds
  } = useAssignDesignInputUserNeed(productId, entityId);
  const {
    updatedProductDesignInput: updatedProductDesignInputDesignOutput,
    error: assignDesignOutputsError,
    assignDesignOutputs
  } = useAssignDesignInputDesignOutput(productId, entityId);
  const {
    updatedProductDesignInput: updatedProductDesignInputVerification,
    error: assignDesignInputVerificationsError,
    assignVerifications: assignDesignInputVerifications
  } = useAssignDesignInputVerification(productId, entityId);
  const {
    updatedProductDesignInput: updatedProductDesignInputValidation,
    error: assignDesignInputValidationsError,
    assignValidations: assignDesignInputValidations
  } = useAssignDesignInputValidation(productId, entityId);
  const {
    updatedProductDesignInput: updatedProductDesignInputDesignSpec,
    error: assignDesignInputDesignSpecsError,
    assignDesignSpecs: assignDesignInputDesignSpecs
  } = useAssignDesignInputDesignSpec(productId, entityId);
  const {
    updatedProductDesignOutput: updatedProductDesignOutputDesignInput,
    error: assignDesignOutputDesignInputsError,
    assignDesignInputs: assignDesignOutputDesignInputs
  } = useAssignDesignOutputDesignInput(productId, entityId);
  const {
    updatedProductDesignOutput: updatedProductDesignOutputVerification,
    error: assignDesignOutputVerificationsError,
    assignVerifications: assignDesignOutputVerifications
  } = useAssignDesignOutputVerification(productId, entityId);
  const {
    updatedProductDesignOutput: updatedProductDesignOutputValidation,
    error: assignDesignOutputValidationsError,
    assignValidations: assignDesignOutputValidations
  } = useAssignDesignOutputValidation(productId, entityId);
  const {
    updatedProductDesignOutput: updatedProductDesignOutputDesignSpec,
    error: assignDesignOutputDesignSpecsError,
    assignDesignSpecs: assignDesignOutputDesignSpecs
  } = useAssignDesignOutputDesignSpec(productId, entityId);
  const {
    updatedProductVerification: updatedProductVerificationDesignOutput,
    error: assignVerificationDesignOutputsError,
    assignDesignOutputs: assignVerificationDesignOutput
  } = useAssignVerificationDesignOutput(productId, entityId);
  const {
    updatedProductValidation: updatedProductValidationUserNeed,
    error: assignValidationUserNeedsError,
    assignUserNeeds: assignValidationUserNeed
  } = useAssignValidationUserNeed(productId, entityId);
  const {
    updatedProductValidation: updatedProductValidationDesignInput,
    error: assignValidationDesignInputsError,
    assignDesignInputs: assignValidationDesignInput
  } = useAssignValidationDesignInput(productId, entityId);
  const {
    updatedProductDesignSpec,
    error: assignDesignSpecUserNeedsError,
    assignUserNeeds: assignDesignSpecUserNeed
  } = useAssignDesignSpecUserNeed(productId, entityId);

  const assign = useCallback(() => {
    const Ids = selectedItems
      ?.filter((designInput: { id: number }) => designInput.id !== unlinkItemId)
      .map((designInput: ProductEntity) => designInput.id);

    if (
      from === ProductBoardColumnEntity.UserNeed &&
      to === ProductBoardColumnEntity.DesignInput
    ) {
      assignUserNeedDesignInputs({
        designInputIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.UserNeed &&
      to === ProductBoardColumnEntity.DesignSpec
    ) {
      assignUserNeedDesignSpecs({
        designSpecIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.UserNeed &&
      to === ProductBoardColumnEntity.Verification
    ) {
      assignUserNeedVerifications({
        verificationIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.UserNeed &&
      to === ProductBoardColumnEntity.Validation
    ) {
      assignUserNeedValidations({
        validationIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignInput &&
      to === ProductBoardColumnEntity.UserNeed
    ) {
      assignUserNeeds({
        userNeedIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignInput &&
      to === ProductBoardColumnEntity.DesignOutput
    ) {
      assignDesignOutputs({
        designOutputIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignInput &&
      to === ProductBoardColumnEntity.Verification
    ) {
      assignDesignInputVerifications({
        verificationIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignInput &&
      to === ProductBoardColumnEntity.Validation
    ) {
      assignDesignInputValidations({
        validationIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignInput &&
      to === ProductBoardColumnEntity.DesignSpec
    ) {
      assignDesignInputDesignSpecs({
        designSpecIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignOutput &&
      to === ProductBoardColumnEntity.DesignInput
    ) {
      assignDesignOutputDesignInputs({
        designInputIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignOutput &&
      to === ProductBoardColumnEntity.Verification
    ) {
      assignDesignOutputVerifications({
        verificationIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignOutput &&
      to === ProductBoardColumnEntity.Validation
    ) {
      assignDesignOutputValidations({
        validationIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignOutput &&
      to === ProductBoardColumnEntity.DesignSpec
    ) {
      assignDesignOutputDesignSpecs({
        designSpecIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.Verification &&
      to === ProductBoardColumnEntity.DesignOutput
    ) {
      assignVerificationDesignOutput({
        designOutputIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.Validation &&
      to === ProductBoardColumnEntity.UserNeed
    ) {
      assignValidationUserNeed({
        userNeedIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.Validation &&
      to === ProductBoardColumnEntity.DesignInput
    ) {
      assignValidationDesignInput({
        designInputIds: Ids
      });
    } else if (
      from === ProductBoardColumnEntity.DesignSpec &&
      to === ProductBoardColumnEntity.UserNeed
    ) {
      assignDesignSpecUserNeed({
        userNeedIds: Ids
      });
    }
  }, [from, to, unlinkItemId, selectedItems]); // from props

  useEffect(() => {
    if (updatedProductUserNeedDesignInput) {
      onChangeRef.current?.(updatedProductUserNeedDesignInput);
    } else if (updatedProductUserNeedDesignSpec) {
      onChangeRef.current?.(updatedProductUserNeedDesignSpec);
    } else if (updatedProductUserNeedVerification) {
      onChangeRef.current?.(updatedProductUserNeedVerification);
    } else if (updatedProductUserNeedValidation) {
      onChangeRef.current?.(updatedProductUserNeedValidation);
    } else if (updatedProductDesignInputUserNeed) {
      onChangeRef.current?.(updatedProductDesignInputUserNeed);
    } else if (updatedProductDesignInputDesignOutput) {
      onChangeRef.current?.(updatedProductDesignInputDesignOutput);
    } else if (updatedProductDesignInputVerification) {
      onChangeRef.current?.(updatedProductDesignInputVerification);
    } else if (updatedProductDesignInputValidation) {
      onChangeRef.current?.(updatedProductDesignInputValidation);
    } else if (updatedProductDesignInputDesignSpec) {
      onChangeRef.current?.(updatedProductDesignInputDesignSpec);
    } else if (updatedProductDesignOutputDesignInput) {
      onChangeRef.current?.(updatedProductDesignOutputDesignInput);
    } else if (updatedProductDesignOutputVerification) {
      onChangeRef.current?.(updatedProductDesignOutputVerification);
    } else if (updatedProductDesignOutputValidation) {
      onChangeRef.current?.(updatedProductDesignOutputValidation);
    } else if (updatedProductDesignOutputDesignSpec) {
      onChangeRef.current?.(updatedProductDesignOutputDesignSpec);
    } else if (updatedProductVerificationDesignOutput) {
      onChangeRef.current?.(updatedProductVerificationDesignOutput);
    } else if (updatedProductValidationUserNeed) {
      onChangeRef.current?.(updatedProductValidationUserNeed);
    } else if (updatedProductValidationDesignInput) {
      onChangeRef.current?.(updatedProductValidationDesignInput);
    } else if (updatedProductDesignSpec) {
      onChangeRef.current?.(updatedProductDesignSpec);
    }
  }, [
    updatedProductUserNeedDesignInput,
    updatedProductUserNeedDesignSpec,
    updatedProductUserNeedVerification,
    updatedProductUserNeedValidation,
    updatedProductDesignInputUserNeed,
    updatedProductDesignInputDesignOutput,
    updatedProductDesignInputVerification,
    updatedProductDesignInputValidation,
    updatedProductDesignInputDesignSpec,
    updatedProductDesignOutputDesignInput,
    updatedProductDesignOutputVerification,
    updatedProductDesignOutputValidation,
    updatedProductDesignOutputDesignSpec,
    updatedProductVerificationDesignOutput,
    updatedProductValidationUserNeed,
    updatedProductValidationDesignInput,
    updatedProductDesignSpec
  ]);

  useEffect(() => {
    if (assignUserNeedDesignInputsError) {
      onErrorRef.current?.(assignUserNeedDesignInputsError);
    } else if (assignUserNeedDesignSpecsError) {
      onErrorRef.current?.(assignUserNeedDesignSpecsError);
    } else if (assignUserNeedVerificationsError) {
      onErrorRef.current?.(assignUserNeedVerificationsError);
    } else if (assignUserNeedValidationsError) {
      onErrorRef.current?.(assignUserNeedValidationsError);
    } else if (assignUserNeedsError) {
      onErrorRef.current?.(assignUserNeedsError);
    } else if (assignDesignOutputsError) {
      onErrorRef.current?.(assignDesignOutputsError);
    } else if (assignDesignInputVerificationsError) {
      onErrorRef.current?.(assignDesignInputVerificationsError);
    } else if (assignDesignInputValidationsError) {
      onErrorRef.current?.(assignDesignInputValidationsError);
    } else if (assignDesignInputDesignSpecsError) {
      onErrorRef.current?.(assignDesignInputDesignSpecsError);
    } else if (assignDesignOutputDesignInputsError) {
      onErrorRef.current?.(assignDesignOutputDesignInputsError);
    } else if (assignDesignOutputVerificationsError) {
      onErrorRef.current?.(assignDesignOutputVerificationsError);
    } else if (assignDesignOutputValidationsError) {
      onErrorRef.current?.(assignDesignOutputValidationsError);
    } else if (assignDesignOutputDesignSpecsError) {
      onErrorRef.current?.(assignDesignOutputDesignSpecsError);
    } else if (assignVerificationDesignOutputsError) {
      onErrorRef.current?.(assignVerificationDesignOutputsError);
    } else if (assignValidationUserNeedsError) {
      onErrorRef.current?.(assignValidationUserNeedsError);
    } else if (assignValidationDesignInputsError) {
      onErrorRef.current?.(assignValidationDesignInputsError);
    } else if (assignDesignSpecUserNeedsError) {
      onErrorRef.current?.(assignDesignSpecUserNeedsError);
    }
  }, [
    assignUserNeedDesignInputsError,
    assignUserNeedDesignSpecsError,
    assignUserNeedVerificationsError,
    assignUserNeedValidationsError,
    assignUserNeedsError,
    assignDesignOutputsError,
    assignDesignInputValidationsError,
    assignDesignInputDesignSpecsError,
    assignDesignOutputDesignInputsError,
    assignDesignOutputVerificationsError,
    assignDesignOutputValidationsError,
    assignDesignOutputDesignSpecsError,
    assignVerificationDesignOutputsError,
    assignValidationUserNeedsError,
    assignValidationDesignInputsError,
    assignDesignSpecUserNeedsError
  ]);

  return (
    <ConfirmDialog
      title={title}
      open={open}
      message={`Are you sure you want to unlink this item?`}
      onCancel={onCancel}
      onConfirm={() => {
        assign();
      }}
    />
  );
};

export default UnlinkConfirmDialog;
